import { useEffect, useState } from "react";
import { X, Table, Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import { HashLoader } from "react-spinners";
import axios from "axios";
import { useAuth, useGoogleAuth } from "./AuthContext";
import { useAuthModal } from "./AuthModalContext";
import googlePicker from "../../utils/googlePicker";
import driveService from "../../utils/driveService";
import {
  saveSheetMapping,
  saveMetricsToUserSheet,
  fetchUserMappings,
  findMappingForUrl,
  formatSheetSaveError,
} from "../../utils/sheetSave";
import {
  getBrowserTimezone,
  getDefaultScheduleDay,
} from "../../utils/scheduleUtils";
import { WeeklySchedulePicker } from "../dashboard/WeeklySchedulePicker";
import { Checkbox } from "../ui/Checkbox";
import { getAvatarFallbackUrl } from "../../utils/helper";
import { trackEvent, AnalyticsEvents } from "../../utils/analytics";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

function TrackInSheetsModal({
  isOpen,
  onClose,
  url,
  metrics,
  allMetrics,
  onTracked,
}) {
  const { isAuthenticated, user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { googleConnected, googleProfile, googleAccessToken, profilePicture } =
    useGoogleAuth();

  const [sheetId, setSheetId] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [tabName, setTabName] = useState("Sheet1");
  const [tabs, setTabs] = useState(["Sheet1"]);
  const [weeklySchedule, setWeeklySchedule] = useState(false);
  const [scheduleDay, setScheduleDay] = useState(getDefaultScheduleDay());
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleRecurrence, setScheduleRecurrence] = useState("weekly");
  const [scheduleNextAt, setScheduleNextAt] = useState(null);
  const [scheduleTimezone] = useState(getBrowserTimezone());
  const [loading, setLoading] = useState(false);
  const [loadingMapping, setLoadingMapping] = useState(true);
  const [existingMapping, setExistingMapping] = useState(null);

  useBodyScrollLock(isOpen);

  const resetScheduleDefaults = () => {
    setWeeklySchedule(false);
    setScheduleDay(getDefaultScheduleDay());
    setScheduleTime("09:00");
    setScheduleRecurrence("weekly");
    setScheduleNextAt(null);
  };

  useEffect(() => {
    if (googleAccessToken) {
      driveService.setAccessToken(googleAccessToken);
      googlePicker.loadPickerAPI(import.meta.env.VITE_GOOGLE_API_KEY, () => {});
    }
  }, [googleAccessToken]);

  useEffect(() => {
    if (!isOpen || !user?.email || !url) {
      setLoadingMapping(false);
      return;
    }
    resetScheduleDefaults();
    setLoadingMapping(true);
    fetchUserMappings(user.email)
      .then((mappings) => {
        const found = findMappingForUrl(mappings, url);
        setExistingMapping(found);
        if (found) {
          setSheetId(found.sheet_id);
          setSheetName(found.sheet_name || found.sheet_id);
          setTabName(found.tab_name || "Sheet1");
          setWeeklySchedule(!!found.schedule_enabled);
          setScheduleDay(
            found.schedule_day_of_week ?? getDefaultScheduleDay(),
          );
          setScheduleTime(found.schedule_time?.slice(0, 5) || "09:00");
          setScheduleRecurrence(found.schedule_recurrence || "weekly");
          setScheduleNextAt(found.next_scheduled_at || null);
        } else {
          setSheetId("");
          setSheetName("");
          setTabName("Sheet1");
          resetScheduleDefaults();
        }
      })
      .catch(() => setExistingMapping(null))
      .finally(() => setLoadingMapping(false));
  }, [isOpen, user?.email, url]);

  useEffect(() => {
    if (!sheetId || !googleAccessToken) return;
    driveService
      .getSheetTabs(sheetId)
      .then((t) => setTabs(t.length ? t : ["Sheet1"]))
      .catch(() => setTabs(["Sheet1"]));
  }, [sheetId, googleAccessToken]);

  const connectGoogle = async () => {
    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/auth/google/set-user`,
        { email: user?.email },
        { withCredentials: true },
      );
    } catch {
      // continue to OAuth
    }
    const returnUrl = window.location.pathname + window.location.search;
    window.location.href = `${
      import.meta.env.VITE_BACKEND_URL
    }/auth/google?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const pickSheet = () => {
    if (!googleAccessToken) {
      toast.error("Connect Google first.");
      return;
    }
    googlePicker.createPicker(googleAccessToken, async (result) => {
      if (!result.success) return;
      try {
        const permission = await driveService.checkSheetPermission(
          result.fileId,
        );
        if (!permission.hasPermission) {
          toast.error(
            "Can't write to that sheet. Pick one you own or can edit.",
          );
          return;
        }
        setSheetId(result.fileId);
        setSheetName(result.fileName);
        setTabName("Sheet1");
        setExistingMapping(null);
        resetScheduleDefaults();
      } catch {
        toast.error("Failed to verify sheet access.");
      }
    });
  };

  const createNewSheet = async () => {
    if (!googleAccessToken) return;
    setLoading(true);
    try {
      const created = await driveService.createNewSheet("PageTest Metrics");
      setSheetId(created.id);
      setSheetName(created.name);
      setTabName("Sheet1");
      setExistingMapping(null);
      resetScheduleDefaults();
      toast.success("New sheet created.");
    } catch {
      toast.error("Failed to create sheet.");
    } finally {
      setLoading(false);
    }
  };

  const saveAllMetrics = async (mappingSheetId, mappingTab) => {
    const toSave = [];
    if (allMetrics?.desktop) toSave.push(allMetrics.desktop);
    if (allMetrics?.mobile) toSave.push(allMetrics.mobile);
    if (!toSave.length && metrics) toSave.push(metrics);

    for (const m of toSave) {
      await saveMetricsToUserSheet(m, mappingSheetId, mappingTab);
    }
  };

  const handleStartTracking = async () => {
    if (!user?.email || !sheetId) {
      toast.error("Pick a Google Sheet first.");
      return;
    }
    setLoading(true);
    try {
      await saveSheetMapping({
        userEmail: user.email,
        url,
        sheetId,
        tabName,
        sheetName,
        scheduleEnabled: weeklySchedule,
        scheduleTime,
        scheduleTimezone,
        scheduleDayOfWeek: scheduleDay,
        scheduleRecurrence,
        nextScheduledAt: scheduleNextAt,
      });
      await saveAllMetrics(sheetId, tabName);
      trackEvent(AnalyticsEvents.SHEET_SAVE, { url, tracked: true });
      toast.success(
        weeklySchedule
          ? scheduleRecurrence === "once"
            ? "Tracking started! One scheduled test saved to your sheet."
            : "Tracking started! Results saved and will run on your schedule."
          : "Tracking started! Results saved to your sheet.",
      );
      onTracked?.();
      onClose();
    } catch (err) {
      toast.error(formatSheetSaveError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNow = async () => {
    const sid = existingMapping?.sheet_id || sheetId;
    const tab = existingMapping?.tab_name || tabName;
    if (!sid) return;
    setLoading(true);
    try {
      await saveAllMetrics(sid, tab);
      trackEvent(AnalyticsEvents.SHEET_SAVE, { url });
      toast.success("Saved to Google Sheet!");
      onClose();
    } catch (err) {
      toast.error(formatSheetSaveError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-card rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Table className="text-green-600" size={22} />
            <h2 className="text-lg font-semibold text-foreground">
              Track in Google Sheets
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-muted text-muted-foreground"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            Save test results to your spreadsheet and re-use the same sheet next
            time you test this URL.
          </p>
          <div className="text-xs bg-muted/50 border rounded-lg px-3 py-2 break-all text-foreground">
            {url}
          </div>

          {!isAuthenticated && (
            <div className="text-center py-4 space-y-3">
              <p className="text-sm text-muted-foreground">Sign in to track results.</p>
              <button
                onClick={() => {
                  onClose();
                  openAuthModal("auth");
                }}
                className="w-full py-2.5 bg-foreground text-background rounded-lg font-medium hover:bg-foreground/90"
              >
                Sign in
              </button>
            </div>
          )}

          {isAuthenticated && loadingMapping && (
            <div className="flex justify-center py-8">
              <HashLoader color="#14b8a6" size={28} />
            </div>
          )}

          {isAuthenticated && !loadingMapping && !googleConnected && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Connect Google to pick a sheet you already use. No service
                account or sheet ID needed.
              </p>
              <button
                onClick={connectGoogle}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-card border border-border rounded-lg font-medium hover:bg-muted/50"
              >
                <GoogleIcon />
                Connect Google Account
              </button>
            </div>
          )}

          {isAuthenticated && !loadingMapping && googleConnected && (
            <>
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                <img
                  src={profilePicture || "/default-avatar.png"}
                  alt=""
                  className="w-6 h-6 rounded-full"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = getAvatarFallbackUrl(
                      googleProfile?.name || user?.email || "User",
                    );
                  }}
                />
                <span>
                  Connected as{" "}
                  <b>{googleProfile?.name || googleProfile?.email}</b>
                </span>
              </div>

              {existingMapping && sheetId && (
                <div className="flex items-start gap-2 p-3 bg-muted/60 border border-border rounded-lg text-sm">
                  <CheckCircle2
                    className="text-brand shrink-0 mt-0.5"
                    size={18}
                  />
                  <div>
                    <p className="font-medium text-foreground">
                      Already tracking
                    </p>
                    <p className="text-brand text-xs mt-0.5">
                      {existingMapping.sheet_name || "Google Sheet"} ·{" "}
                      {existingMapping.tab_name}
                    </p>
                  </div>
                </div>
              )}

              {sheetName ? (
                <div className="border rounded-lg p-3 bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    Selected sheet
                  </p>
                  <p className="font-medium text-foreground truncate">
                    {sheetName}
                  </p>
                  {tabs.length > 1 ? (
                    <label className="block mt-2 text-sm">
                      <span className="text-muted-foreground">Tab</span>
                      <select
                        value={tabName}
                        onChange={(e) => setTabName(e.target.value)}
                        className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
                      >
                        {tabs.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">Tab: {tabName}</p>
                  )}
                  <button
                    type="button"
                    onClick={pickSheet}
                    className="mt-2 text-xs text-brand hover:underline"
                  >
                    Change sheet
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    onClick={pickSheet}
                    disabled={loading}
                    className="py-2.5 px-3 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 disabled:opacity-60"
                  >
                    Pick existing sheet
                  </button>
                  <button
                    onClick={createNewSheet}
                    disabled={loading}
                    className="py-2.5 px-3 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60"
                  >
                    Create new sheet
                  </button>
                </div>
              )}

              {!existingMapping && sheetId && (
                <div className="space-y-3">
                  <Checkbox
                    checked={weeklySchedule}
                    onCheckedChange={setWeeklySchedule}
                    align="start"
                    className="p-3 rounded-lg hover:bg-muted/50 w-full"
                    labelClassName="w-full"
                  >
                    <div>
                      <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                        <Calendar size={16} />
                        Schedule automatic tests
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 font-normal">
                        Re-run on a schedule (weekly by default).
                      </p>
                    </div>
                  </Checkbox>
                  {weeklySchedule && (
                    <div className="rounded-lg border border-border bg-muted/30 p-3">
                      <WeeklySchedulePicker
                        variant="inline"
                        dayOfWeek={scheduleDay}
                        time={scheduleTime}
                        recurrence={scheduleRecurrence}
                        timezone={scheduleTimezone}
                        nextScheduledAt={scheduleNextAt}
                        onSave={({
                          scheduleDayOfWeek,
                          scheduleTime: nextTime,
                          scheduleRecurrence: nextRecurrence,
                          nextScheduledAt,
                        }) => {
                          setScheduleDay(scheduleDayOfWeek);
                          setScheduleTime(nextTime);
                          setScheduleRecurrence(nextRecurrence || "weekly");
                          setScheduleNextAt(nextScheduledAt || null);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-2">
                {existingMapping && sheetId === existingMapping.sheet_id ? (
                  <button
                    onClick={handleSaveNow}
                    disabled={loading}
                    className="w-full py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-60"
                  >
                    {loading ? "Saving…" : "Save latest results"}
                  </button>
                ) : (
                  sheetId && (
                    <button
                      onClick={handleStartTracking}
                      disabled={loading}
                      className="w-full py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-60"
                    >
                      {loading ? "Saving…" : "Start tracking & save results"}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export default TrackInSheetsModal;
